import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Row, Col, Typography, Breadcrumb, message, Spin } from 'antd';
import { ArrowLeftOutlined, HomeOutlined, PrinterOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardService from '../../services/DashboardService';
import JsBarcode from 'jsbarcode';

const { Title, Text } = Typography;

const ViewKanbanPage = () => {
  const { kanbanNumber } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [kanbanData, setKanbanData] = useState(null);
  const barcodeRef = useRef(null);

  useEffect(() => {
    if (kanbanNumber) {
      loadKanbanData();
    }
  }, [kanbanNumber]);

  useEffect(() => {
    if (kanbanData && barcodeRef.current) {
      generateBarcode();
    }
  }, [kanbanData]);

  const generateBarcode = () => {
    if (barcodeRef.current && kanbanData?.kb_number) {
      try {
        JsBarcode(barcodeRef.current, kanbanData.kb_number, {
          format: "CODE128",
          width: 2,
          height: 50,
          displayValue: false,
          margin: 0
        });
      } catch (error) {
        console.error('Error generating barcode:', error);
      }
    }
  };

  const captureAndUploadBarcode = async () => {
    if (!barcodeRef.current || !kanbanData?.kb_number) {
      console.error('Barcode canvas or kanban number not available');
      return;
    }

    try {
      // Convert canvas to blob
      const canvas = barcodeRef.current;
      const blob = await new Promise(resolve => {
        canvas.toBlob(resolve, 'image/png');
      });

      if (!blob) {
        throw new Error('Failed to convert canvas to blob');
      }

      // Create a File object from the blob
      const fileName = `barcode_${kanbanData.kb_number}.png`;
      const barcodeFile = new File([blob], fileName, { type: 'image/png' });

      // Upload the barcode image
      const response = await DashboardService.uploadBarcodeImage(kanbanData.kb_number, barcodeFile);
      
      if (response.status) {
        console.log('Barcode uploaded successfully:', response);
        message.success('Barcode image uploaded successfully');
      } else {
        console.error('Failed to upload barcode:', response.message);
        message.error('Failed to upload barcode image');
      }
    } catch (error) {
      console.error('Error capturing and uploading barcode:', error);
      message.error('Error uploading barcode image');
    }
  };

  const loadKanbanData = async () => {
    try {
      setLoading(true);
      const response = await DashboardService.getKanbanDataInSupermarket(kanbanNumber);
      
      if (response.status && response.data) {
        setKanbanData(response.data);
      } else {
        message.error(response.message || 'Failed to load kanban data');
       
      }
    } catch (error) {
      message.error('Error loading kanban data');
      console.error('Error loading kanban data:', error);
     
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    try {
      // First, capture and upload the barcode image
      await captureAndUploadBarcode();
      
      // Then open the print window
      const printWindow = window.open('', '_blank');
      const printContent = generatePrintContent();
      
      printWindow.document.write(`
      <html>
        <head>
          <title>Print Master Kanban - ${kanbanData?.kb_number}</title>
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 0; 
              padding: 20px; 
              background: white;
            }
            .kanban-label {
              width: 300px;
              height: 200px;
              border: 2px solid #ccc;
              margin: 10px;
              padding: 10px;
              display: inline-block;
              vertical-align: top;
              page-break-inside: avoid;
            }
            .header {
              background: #f0f0f0;
              padding: 5px;
              margin: -10px -10px 10px -10px;
              display: flex;
              justify-content: space-between;
            }
            .size-table {
              width: 100%;
              border-collapse: collapse;
              margin: 10px 0;
            }
            .size-table th, .size-table td {
              border: 1px solid #ccc;
              padding: 2px 4px;
              text-align: center;
              font-size: 12px;
            }
            .size-table th {
              background: #f0f0f0;
              font-weight: bold;
            }
            .barcode {
              text-align: center;
              margin: 10px 0;
            }
            .barcode-number {
              font-size: 16px;
              font-weight: bold;
              margin-top: 5px;
            }
            .field-row {
              display: flex;
              justify-content: space-between;
              margin: 5px 0;
              font-size: 12px;
            }
            .field-label {
              font-weight: bold;
            }
            .size-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 5px;
              margin: 10px 0;
            }
            .size-item {
              display: flex;
              justify-content: space-between;
              font-size: 12px;
            }
            @media print {
              body { margin: 0; padding: 0; }
              .kanban-label { 
                margin: 0; 
                border: 1px solid #000;
                width: 300px;
                height: 200px;
              }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    } catch (error) {
      console.error('Error printing kanban:', error);
      message.error('Error printing kanban');
    }
  };

  const generatePrintContent = () => {
    if (!kanbanData) return '';

    const sizeData = kanbanData.size_data || {};
    const sizes = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'];
    const total = Object.values(sizeData).reduce((sum, qty) => sum + (parseInt(qty) || 0), 0);

    return `
      <div class="kanban-label">
        <div class="header">
          <span class="field-label">Item No: ${kanbanData.item_no || ''}</span>
          <span class="field-label">Kanban: ${kanbanData.kb_number || ''}</span>
        </div>
        
        <div class="field-row">
          <span class="field-label">PO No: ${kanbanData.po_no || ''}</span>
        </div>
        
        <div class="field-row">
          <span class="field-label">Color: ${kanbanData.po_color || 'N/A'}</span>
        </div>
        
        <div class="size-grid">
          <div class="size-item">
            <span class="field-label">Country: ${kanbanData.country || ''}</span>
            <span class="field-label">Line No:</span>
          </div>
          <div class="size-item">
            <span class="field-label">Date:</span>
            <span class="field-label">Total ${total}</span>
          </div>
        </div>
        
        <table class="size-table">
          <thead>
            <tr>
              ${sizes.map(size => `<th>${size}</th>`).join('')}
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              ${sizes.map(size => `<td>${sizeData[size] || ''}</td>`).join('')}
              <td>${total}</td>
            </tr>
          </tbody>
        </table>
        
        <div class="barcode">
          <svg id="barcode-print-1" width="200" height="50"></svg>
          <div class="barcode-number">${kanbanData.kb_number || ''}</div>
        </div>
      </div>
      
      <div class="kanban-label">
        <div class="header">
          <span class="field-label">Item No: ${kanbanData.item_no || ''}</span>
          <span class="field-label">Kanban: ${kanbanData.kb_number || ''}</span>
        </div>
        
        <div class="field-row">
          <span class="field-label">PO No: ${kanbanData.po_no || ''}</span>
        </div>
        
        <div class="field-row">
          <span class="field-label">Color: ${kanbanData.po_color || 'N/A'}</span>
        </div>
        
        <div class="size-grid">
          <div class="size-item">
            <span class="field-label">Country: ${kanbanData.country || ''}</span>
            <span class="field-label">Line No:</span>
          </div>
          <div class="size-item">
            <span class="field-label">Date:</span>
            <span class="field-label">Total ${total}</span>
          </div>
        </div>
        
        <table class="size-table">
          <thead>
            <tr>
              ${sizes.map(size => `<th>${size}</th>`).join('')}
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              ${sizes.map(size => `<td>${sizeData[size] || ''}</td>`).join('')}
              <td>${total}</td>
            </tr>
          </tbody>
        </table>
        
        <div class="barcode">
          <svg id="barcode-print-2" width="200" height="50"></svg>
          <div class="barcode-number">${kanbanData.kb_number || ''}</div>
        </div>
      </div>
      
      <script>
        // Generate barcodes for both labels
        try {
          JsBarcode("#barcode-print-1", "${kanbanData.kb_number || ''}", {
            format: "CODE128",
            width: 2,
            height: 50,
            displayValue: false,
            margin: 0
          });
          JsBarcode("#barcode-print-2", "${kanbanData.kb_number || ''}", {
            format: "CODE128",
            width: 2,
            height: 50,
            displayValue: false,
            margin: 0
          });
        } catch (error) {
          console.error('Error generating barcodes:', error);
        }
      </script>
    `;
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading kanban details...</div>
      </div>
    );
  }

  if (!kanbanData) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Title level={3}>Kanban not found</Title>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    );
  }

  const sizeData = kanbanData.size_data || {};
  const sizes = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'];
  const total = Object.values(sizeData).reduce((sum, qty) => sum + (parseInt(qty) || 0), 0);

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Breadcrumb style={{ marginBottom: '16px' }}>
          <Breadcrumb.Item>
            <HomeOutlined style={{ marginRight: '4px' }} />
            Home
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Kanban</Breadcrumb.Item>
        </Breadcrumb>
        <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
          <ArrowLeftOutlined 
            style={{ marginRight: '8px', cursor: 'pointer' }} 
            
          />
          View Kanban - [{kanbanData.kb_number}]
        </Title>
      </div>

      <Row gutter={[24, 24]}>
        {/* Left Column - Kanban Details */}
        <Col xs={24} lg={14}>
          <Card title="Kanban Details" style={{ marginBottom: '24px' }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Card size="small" style={{ backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}>
                  <Text strong>Purchase Order Number:</Text><br />
                  <Text>{kanbanData.po_no || 'N/A'}</Text>
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card size="small" style={{ backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}>
                  <Text strong>Purchase Order Date:</Text><br />
                  <Text>{kanbanData.po_date || 'N/A'}</Text>
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card size="small" style={{ backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}>
                  <Text strong>Colour:</Text><br />
                  <Text>{kanbanData.po_color || 'N/A'}</Text>
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card size="small" style={{ backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}>
                  <Text strong>Total quantity:</Text><br />
                  <Text>{kanbanData.po_total_quantity || 'N/A'}</Text>
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card size="small" style={{ backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}>
                  <Text strong>Item Number:</Text><br />
                  <Text>{kanbanData.item_no || 'N/A'}</Text>
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card size="small" style={{ backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}>
                  <Text strong>Customer:</Text><br />
                  <Text>{kanbanData.customer || 'N/A'}</Text>
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card size="small" style={{ backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}>
                  <Text strong>Priority:</Text><br />
                  <Text>{kanbanData.po_priority || 'N/A'}</Text>
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card size="small" style={{ backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}>
                  <Text strong>Country:</Text><br />
                  <Text>{kanbanData.country || 'N/A'}</Text>
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card size="small" style={{ backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}>
                  <Text strong>Status:</Text><br />
                  <Text>{kanbanData.status || 'N/A'}</Text>
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card size="small" style={{ backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}>
                  <Text strong>BarCode:</Text><br />
                  <Text>{kanbanData.kb_number || 'N/A'}</Text>
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card size="small" style={{ backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}>
                  <Text strong>Ship Date:</Text><br />
                  <Text>{kanbanData.po_ship_date || 'N/A'}</Text>
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card size="small" style={{ backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}>
                  <Text strong>Ship Perc:</Text><br />
                  <Text>0</Text>
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Right Column - Printable Kanban */}
        <Col xs={24} lg={10}>
          <Card 
            title="Master Kanban" 
            style={{ 
              backgroundColor: 'white', 
              border: '1px solid #d9d9d9',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            <div style={{ 
              border: '1px solid #ccc', 
              padding: '15px', 
              backgroundColor: 'white',
              minHeight: '400px'
            }}>
              {/* Header */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: '10px',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                <span>Item No: {kanbanData.item_no || ''}</span>
                <span>Kanban: {kanbanData.kb_number || ''}</span>
              </div>
              
              {/* PO and Color */}
              <div style={{ marginBottom: '10px', fontSize: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                  <span><strong>PO No:</strong> {kanbanData.po_no || ''}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                  <span><strong>Color:</strong> {kanbanData.po_color || 'N/A'}</span>
                </div>
              </div>
              
              {/* Country and Line */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '10px', 
                marginBottom: '15px',
                fontSize: '12px'
              }}>
                <div>
                  <div><strong>Country:</strong> {kanbanData.country || ''}</div>
                  <div><strong>L:</strong> {sizeData.L || ''}</div>
                </div>
                <div>
                  <div><strong>Line No:</strong></div>
                  <div><strong>Date:</strong></div>
                  <div><strong>Total:</strong> {total}</div>
                </div>
              </div>
              
              {/* Size Table */}
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse', 
                marginBottom: '15px',
                fontSize: '10px'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f0f0f0' }}>
                    {sizes.map(size => (
                      <th key={size} style={{ border: '1px solid #ccc', padding: '2px', textAlign: 'center' }}>
                        {size}
                      </th>
                    ))}
                    <th style={{ border: '1px solid #ccc', padding: '2px', textAlign: 'center' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {sizes.map(size => (
                      <td key={size} style={{ border: '1px solid #ccc', padding: '2px', textAlign: 'center' }}>
                        {sizeData[size] || ''}
                      </td>
                    ))}
                    <td style={{ border: '1px solid #ccc', padding: '2px', textAlign: 'center' }}>
                      {total}
                    </td>
                  </tr>
                </tbody>
              </table>
              
              {/* Barcode */}
              <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                <canvas ref={barcodeRef} style={{ maxWidth: '100%', height: 'auto' }}></canvas>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: 'bold',
                  letterSpacing: '2px',
                  marginTop: '5px'
                }}>
                  {kanbanData.kb_number || ''}
                </div>
              </div>
              
              {/* Print Button */}
              <div style={{ textAlign: 'center' }}>
                <Button 
                  type="primary" 
                  size="large"
                  icon={<PrinterOutlined />}
                  onClick={handlePrint}
                  style={{ 
                    backgroundColor: '#ff4d4f', 
                    borderColor: '#ff4d4f',
                    borderRadius: '4px',
                    fontWeight: 'bold'
                  }}
                >
                  Print Master Kanban
                </Button>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ViewKanbanPage;
